import { useMemo } from 'react';
import { slotLabel, type AssignmentSlotValue } from '@my/tms-api';
import { useOrderResources } from '@my/tms-api/hooks';
import { DataCell } from './badges';

/** Slots shown, in the order a planner fills them. */
const SLOT_ORDER: { slot: AssignmentSlotValue; label: string }[] = [
  { slot: 'Driver', label: 'Driver' },
  { slot: 'CoDriver', label: 'Co-driver' },
  { slot: 'Vehicle', label: 'Vehicle' },
  { slot: 'Trailer', label: 'Trailer' },
  { slot: 'Container', label: 'Container' },
];

/**
 * Read-only view of who and what is doing this task.
 *
 * <p>
 * Display only — assignment is made from the plan cockpit (select a job, then the
 * resource card's Assign, or drag the card onto the row). This is here so the
 * order screen can answer "who's on this?" without sending anyone to another
 * screen, and it renders nothing at all when the task is unassigned rather than
 * showing a row of empty labels.
 * </p>
 */
export function TaskAssignmentPanel({ orderId, taskId }: { orderId: string; taskId: string }) {
  const assignmentsQuery = useOrderResources(orderId);

  /** This task's rows only — the query returns the whole order. */
  const mine = useMemo(
    () => (assignmentsQuery.data ?? []).filter((r) => r.orderTaskId === taskId),
    [assignmentsQuery.data, taskId],
  );

  const filled = SLOT_ORDER.map(({ slot, label }) => {
    // Whole-task rows only. A cargo-item-scoped assignment belongs to part of the
    // load, so it isn't this stop's crew — noted separately below.
    const row = mine.find(
      (r) => r.slot === slot && r.slotIndex === 1 && r.orderTaskCargoItemId == null,
    );
    const value = slotLabel(row);
    return value ? { label, value, isSubcontractor: row!.isSubcontractor } : null;
  }).filter((x): x is { label: string; value: string; isSubcontractor: boolean } => x !== null);

  const splitCount = mine.filter((r) => r.orderTaskCargoItemId != null).length;

  // Nothing assigned — say nothing. An empty panel of dashes would be noise on a
  // screen that already has plenty.
  if (filled.length === 0 && splitCount === 0) return null;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
      <div className="mb-1.5 flex items-center gap-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Assignment
        </h4>
        {filled.some((f) => f.isSubcontractor) && (
          <span className="rounded bg-yellow-100 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800">
            Subcontracted
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-2">
        {filled.map((f) => (
          <DataCell key={f.label} label={f.label} value={f.value} />
        ))}
      </div>

      {splitCount > 0 && (
        <p className="mt-1.5 text-[11px] italic text-slate-500">
          Some cargo on this stop is assigned separately.
        </p>
      )}
    </div>
  );
}
